import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle, ArrowRight, Building2, Check, ChevronLeft, ChevronRight, Link2, Loader2, Lock, Mail, Pencil, Plus,
  RefreshCw, Search, Shield, Sparkles, Trash2, Unlink, Unlock, UserCheck,
  Users as UsersIcon, X,
} from 'lucide-react';
import http from '../api/http';
import { useAuth } from '../context/AuthContext';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';
import { ROLE_LABELS } from '../data/filiales';

/* ═══════════════════════════ Constantes ═══════════════════════════ */

// ⚠️ À vérifier : valeur de `etat` qui correspond à un compte ACTIF dans votre base.
const ETAT_ACTIF = 0;

const FALLBACK_FILIALES = [
  { id: 1, label: 'PALLADIUM TECH' },
  { id: 2, label: 'Media Contact CI' },
  { id: 3, label: 'Media Contact CM' },
  { id: 4, label: 'Media Contact GN' },
  { id: 5, label: 'Media Contact GNB' },
  { id: 6, label: 'Media Contact CG' },
];

const EMPTY_FORM = {
  username: '', email: '', password: '', nom: '', prenom: '',
  idprofil: '', role: 'user', filiale_id: '', personnel_id: '',
};

const ROLE_META = {
  super_admin: { badge: 'bg-blue-50 text-blue-700 ring-blue-200', bar: 'from-blue-500 to-cyan-500' },
  admin_pays: { badge: 'bg-sky-50 text-sky-800 ring-sky-200', bar: 'from-sky-500 to-indigo-500' },
  manager: { badge: 'bg-emerald-50 text-emerald-700 ring-emerald-200', bar: 'from-emerald-500 to-teal-500' },
  user: { badge: 'bg-slate-100 text-slate-700 ring-slate-200', bar: 'from-slate-300 to-slate-400' },
};

const AVATAR_GRADIENTS = [
  'from-blue-500 to-cyan-500',
  'from-sky-500 to-indigo-500',
  'from-emerald-500 to-teal-500',
  'from-amber-500 to-orange-500',
  'from-rose-500 to-pink-500',
  'from-cyan-500 to-blue-500',
];

const inputCls =
  'w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100';

/* ═══════════════════════════ Helpers ═══════════════════════════ */

const getInitials = (nom, prenom) =>
  `${(nom || '?').trim()[0] || '?'}${(prenom || '').trim()[0] || ''}`.toUpperCase();

const gradientFor = (seed) => {
  const str = String(seed ?? '');
  let h = 0;
  for (let i = 0; i < str.length; i += 1) h = (h * 31 + str.charCodeAt(i)) | 0;
  return AVATAR_GRADIENTS[Math.abs(h) % AVATAR_GRADIENTS.length];
};

const roleLabel = (role) => ROLE_LABELS?.[role] || role || '—';
const isUserActive = (u) => u.etat === ETAT_ACTIF;

/* ═══════════════════════════ Petits composants ═══════════════════════════ */

const Avatar = ({ nom, prenom, seed, size = 'md' }) => {
  const sizes = {
    sm: 'h-9 w-9 rounded-xl text-xs',
    md: 'h-12 w-12 rounded-2xl text-sm',
    lg: 'h-20 w-20 rounded-3xl text-xl',
  };
  return (
    <span
      className={`flex shrink-0 items-center justify-center bg-gradient-to-br font-bold text-white shadow-md ${sizes[size]} ${gradientFor(seed ?? nom)}`}
    >
      {getInitials(nom, prenom)}
    </span>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</span>
    {children}
  </label>
);

const Modal = ({ children, onClose, maxWidth = 'max-w-2xl', labelledBy }) => {
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div
      className="u-fade fixed inset-0 z-50 flex items-end justify-center bg-slate-900/60 p-0 backdrop-blur-sm sm:items-center sm:p-6"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        className={`u-rise flex max-h-[94vh] w-full ${maxWidth} flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl ring-1 ring-black/5 sm:rounded-3xl`}
      >
        {children}
      </div>
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon, tone, onClick, active, hint, children }) => {
  const tones = {
    blue: { icon: 'bg-blue-100 text-blue-600', value: 'text-slate-900' },
    emerald: { icon: 'bg-emerald-100 text-emerald-600', value: 'text-emerald-600' },
    sky: { icon: 'bg-sky-100 text-sky-600', value: 'text-sky-600' },
    amber: { icon: 'bg-amber-100 text-amber-600', value: 'text-amber-600' },
  }[tone];
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={`rounded-3xl border bg-white p-5 text-left shadow-sm transition-all duration-200 ${
        onClick ? 'cursor-pointer hover:-translate-y-0.5 hover:shadow-md' : ''
      } ${active ? 'border-blue-400 ring-4 ring-blue-100' : 'border-slate-200'}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500">{label}</p>
          <p className={`mt-1 text-3xl font-bold ${tones.value}`}>{value}</p>
          {hint && <p className="mt-0.5 text-xs text-slate-400">{hint}</p>}
        </div>
        <span className={`flex h-12 w-12 items-center justify-center rounded-2xl ${tones.icon}`}>
          <Icon className="h-6 w-6" />
        </span>
      </div>
      {children}
    </Tag>
  );
};

/* ═══════════════════════════ Pont de rattachement ═══════════════════════════ */

const BridgeNode = ({ children, label, sub }) => (
  <div className="flex w-28 shrink-0 flex-col items-center text-center sm:w-36">
    {children}
    <p className="mt-3 w-full truncate text-sm font-semibold text-slate-900">{label}</p>
    <p className="w-full truncate text-xs text-slate-500">{sub}</p>
  </div>
);

const LinkBridge = ({ user, employee }) => {
  const linked = !!employee;
  return (
    <div className="relative overflow-hidden border-b border-slate-100 bg-gradient-to-b from-slate-50 via-white to-white px-4 py-7 sm:px-10">
      <div className="pointer-events-none absolute inset-0 opacity-70 [background-image:radial-gradient(circle_at_50%_0%,rgba(139,92,246,0.10),transparent_60%)]" />
      <div className="relative flex items-start justify-center">
        <BridgeNode label={`${user.nom ?? ''} ${user.prenom ?? ''}`} sub={user.email || user.username}>
          <Avatar nom={user.nom} prenom={user.prenom} seed={user.iduser} size="lg" />
        </BridgeNode>

        {/* Connecteur */}
        <div className="relative mx-2 mt-[2.35rem] h-2 flex-1 sm:mx-5">
          {linked ? (
            <div className="u-grow relative h-2 overflow-hidden rounded-full bg-gradient-to-r from-blue-500 via-cyan-500 to-emerald-500 shadow-[0_0_18px_rgba(59,130,246,0.45)]">
              <span className="u-shine absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/70 to-transparent" />
            </div>
          ) : (
            <div
              className="u-flow h-2 rounded-full"
              style={{
                backgroundImage: 'repeating-linear-gradient(90deg,#cbd5e1 0 10px,transparent 10px 20px)',
                backgroundSize: '20px 100%',
              }}
            />
          )}
          <span
            key={employee ? employee.idpersonnel : 'none'}
            className={`absolute left-1/2 top-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-4 border-white shadow-lg ${
              linked ? 'u-pop bg-emerald-500 text-white' : 'bg-slate-200 text-slate-500'
            }`}
          >
            {!linked && <span className="u-ping absolute inset-0 rounded-full bg-blue-300/60" />}
            <Link2 className="relative h-5 w-5" />
          </span>
        </div>

        {linked ? (
          <BridgeNode
            label={`${employee.nom ?? ''} ${employee.prenom ?? ''}`}
            sub={employee.matricule ? `Matricule ${employee.matricule}` : 'Employé'}
          >
            <span key={employee.idpersonnel} className="u-pop">
              <Avatar nom={employee.nom} prenom={employee.prenom} seed={employee.idpersonnel} size="lg" />
            </span>
          </BridgeNode>
        ) : (
          <BridgeNode label="Employé à choisir" sub="Sélectionnez ci-dessous">
            <span className="flex h-20 w-20 items-center justify-center rounded-3xl border-2 border-dashed border-slate-300 bg-white text-slate-300">
              <UsersIcon className="h-8 w-8" />
            </span>
          </BridgeNode>
        )}
      </div>
    </div>
  );
};

const AttachModal = ({ user, personnel, takenBy, saving, onClose, onConfirm, onDetach }) => {
  const [selectedId, setSelectedId] = useState(String(user.personnel_id ?? ''));
  const [query, setQuery] = useState('');
  const [sameFiliale, setSameFiliale] = useState(false);

  const currentId = String(user.personnel_id ?? '');
  const canFilterFiliale = !!user.filiale_id && personnel.some((p) => p.filiale_id != null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return personnel
      .filter((p) => {
        if (sameFiliale && String(p.filiale_id) !== String(user.filiale_id)) return false;
        if (!q) return true;
        return [p.nom, p.prenom, p.matricule, p.poste, p.fonction].some((v) =>
          String(v ?? '').toLowerCase().includes(q)
        );
      })
      .sort((a, b) => `${a.nom} ${a.prenom}`.localeCompare(`${b.nom} ${b.prenom}`));
  }, [personnel, query, sameFiliale, user.filiale_id]);

  const visible = filtered.slice(0, 60);
  const selectedEmployee = personnel.find((p) => String(p.idpersonnel) === selectedId) || null;
  const unchanged = selectedId === currentId;

  return (
    <Modal onClose={onClose} maxWidth="max-w-3xl" labelledBy="attach-title">
      {/* En-tête */}
      <div className="flex items-start justify-between gap-4 px-6 pb-4 pt-6">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-600 text-white shadow-lg shadow-blue-500/30">
            <Link2 className="h-5 w-5" />
          </span>
          <div>
            <h2 id="attach-title" className="text-lg font-bold text-slate-900">Rattacher à un employé</h2>
            <p className="text-sm text-slate-500">Reliez ce compte à la fiche de l'employé correspondant.</p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer"
          className="rounded-xl p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <LinkBridge user={user} employee={selectedEmployee} />

      {/* Sélecteur */}
      <div className="flex min-h-0 flex-1 flex-col px-6 pt-5">
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <div className="relative min-w-[200px] flex-1">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher par nom, prénom ou matricule…"
              className={`${inputCls} pl-10`}
            />
          </div>
          {canFilterFiliale && (
            <button
              type="button"
              onClick={() => setSameFiliale((v) => !v)}
              className={`inline-flex items-center gap-2 rounded-xl border px-3.5 py-2.5 text-sm font-medium transition ${
                sameFiliale
                  ? 'border-blue-500 bg-blue-100 text-blue-700'
                  : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
              }`}
            >
              <Building2 className="h-4 w-4" /> Même filiale
            </button>
          )}
        </div>

        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          {filtered.length} employé{filtered.length > 1 ? 's' : ''}
        </p>

        <div className="max-h-[260px] min-h-[120px] flex-1 overflow-y-auto pb-4 pr-1">
          {filtered.length === 0 ? (
            <div className="flex h-28 flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-200 text-sm text-slate-400">
              <Search className="mb-1 h-5 w-5" />
              Aucun employé ne correspond
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              {visible.map((p) => {
                const id = String(p.idpersonnel);
                const owner = takenBy[id] && takenBy[id].iduser !== user.iduser ? takenBy[id] : null;
                const isSel = id === selectedId;
                const isCurrent = id === currentId;
                return (
                  <button
                    key={id}
                    type="button"
                    disabled={!!owner}
                    onClick={() => setSelectedId(isSel ? '' : id)}
                    className={`flex items-center gap-3 rounded-2xl border-2 p-3 text-left transition-all duration-200 ${
                      isSel
                        ? 'border-blue-500 bg-blue-50 shadow-md shadow-blue-500/10'
                        : owner
                        ? 'cursor-not-allowed border-slate-100 bg-slate-50 opacity-60'
                        : 'border-slate-200 bg-white hover:-translate-y-0.5 hover:border-blue-300 hover:shadow-md'
                    }`}
                  >
                    <Avatar nom={p.nom} prenom={p.prenom} seed={p.idpersonnel} size="sm" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-slate-900">
                        {p.nom} {p.prenom}
                      </span>
                      <span className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-500">
                        {p.matricule && (
                          <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-slate-600">
                            {p.matricule}
                          </span>
                        )}
                        {(p.poste || p.fonction) && <span className="truncate">{p.poste || p.fonction}</span>}
                      </span>
                      {owner && (
                        <span className="mt-1 block truncate text-[11px] font-medium text-amber-600">
                          Déjà rattaché à {owner.nom} {owner.prenom}
                        </span>
                      )}
                    </span>
                    {isCurrent && !isSel && (
                      <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                        Actuel
                      </span>
                    )}
                    {isSel && (
                      <span className="u-pop flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white">
                        <Check className="h-3.5 w-3.5" strokeWidth={3} />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          {filtered.length > visible.length && (
            <p className="mt-3 text-center text-xs text-slate-400">
              {filtered.length - visible.length} autres résultats — affinez votre recherche pour les voir.
            </p>
          )}
        </div>
      </div>

      {/* Pied */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 bg-slate-50/80 px-6 py-4">
        {user.personnel_id ? (
          <button
            type="button"
            onClick={onDetach}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-50 disabled:opacity-50"
          >
            <Unlink className="h-4 w-4" /> Détacher
          </button>
        ) : (
          <span />
        )}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
          >
            Annuler
          </button>
          <button
            type="button"
            disabled={!selectedId || unchanged || saving}
            onClick={() => onConfirm(selectedId)}
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-500/25 transition hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Link2 className="h-4 w-4" />
            )}
            {selectedEmployee && !unchanged ? `Rattacher à ${selectedEmployee.prenom || selectedEmployee.nom}` : unchanged && selectedId ? 'Déjà rattaché' : 'Choisir un employé'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

/* ═══════════════════════════ Page ═══════════════════════════ */

const Users = () => {
  const { user } = useAuth();
  const { filialeId, isConsolidated, assignedFiliales } = useFiliale();

  const [usersList, setUsersList] = useState([]);
  const [profils, setProfils] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [attachUser, setAttachUser] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [profilFilter, setProfilFilter] = useState('');
  const [attachFilter, setAttachFilter] = useState(''); // '' | 'attached' | 'unattached'
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  const [toast, setToast] = useState(null);
  const [justLinked, setJustLinked] = useState(null);

  const isSuperAdmin = user?.role === 'super_admin';
  const canAttach = isSuperAdmin;

  const showToast = useCallback((type, message) => setToast({ type, message, id: Date.now() }), []);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 3800);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    if (justLinked == null) return undefined;
    const t = setTimeout(() => setJustLinked(null), 2600);
    return () => clearTimeout(t);
  }, [justLinked]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const endpoint =
          isSuperAdmin && (!filialeId || isConsolidated)
            ? '/api/users'
            : `/api/users/filiale/${filialeId}`;
        const response = await http.get(endpoint);
        setUsersList(Array.isArray(response.data) ? response.data : response.data?.data || []);

        const profilsResponse = await http.get('/api/profils');
        setProfils(Array.isArray(profilsResponse.data) ? profilsResponse.data : []);

        if (isSuperAdmin) {
          const personnelResponse = await http.get('/api/personnel');
          setPersonnel(Array.isArray(personnelResponse.data?.data) ? personnelResponse.data.data : []);
        }
      } catch (error) {
        setUsersList([]);
        setProfils([]);
        setPersonnel([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isSuperAdmin, filialeId, isConsolidated, refreshKey]);

  /* ─── Données dérivées ─── */

  const personnelById = useMemo(() => {
    const map = {};
    personnel.forEach((p) => { map[String(p.idpersonnel)] = p; });
    return map;
  }, [personnel]);

  const takenBy = useMemo(() => {
    const map = {};
    usersList.forEach((u) => { if (u.personnel_id) map[String(u.personnel_id)] = u; });
    return map;
  }, [usersList]);

  const stats = useMemo(() => {
    const total = usersList.length;
    const attached = usersList.filter((u) => u.personnel_id).length;
    return {
      total,
      active: usersList.filter(isUserActive).length,
      attached,
      unattached: total - attached,
      rate: total ? Math.round((attached / total) * 100) : 0,
    };
  }, [usersList]);

  const roleCounts = useMemo(() => {
    const counts = {};
    usersList.forEach((u) => { counts[u.role] = (counts[u.role] || 0) + 1; });
    return counts;
  }, [usersList]);

  const filteredUsers = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return usersList.filter((u) => {
      const matchesSearch =
        !q || [u.nom, u.prenom, u.email, u.username].some((v) => String(v ?? '').toLowerCase().includes(q));
      const matchesRole = !roleFilter || u.role === roleFilter;
      const matchesProfil = !profilFilter || String(u.idprofil) === String(profilFilter);
      const matchesAttach =
        !attachFilter || (attachFilter === 'attached' ? !!u.personnel_id : !u.personnel_id);
      return matchesSearch && matchesRole && matchesProfil && matchesAttach;
    });
  }, [usersList, searchTerm, roleFilter, profilFilter, attachFilter]);

  // Réinitialiser la page lorsque les filtres changent
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, roleFilter, profilFilter, attachFilter]);

  // Calculer les éléments paginés
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredUsers.slice(startIndex, endIndex);
  }, [filteredUsers, currentPage]);

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  const filialeOptions = useMemo(
    () =>
      assignedFiliales && assignedFiliales.length > 0
        ? assignedFiliales.map((f) => ({ id: f.id, label: f.libelle }))
        : FALLBACK_FILIALES,
    [assignedFiliales]
  );

  const getProfilLabel = (idprofil) => {
    const profil = profils.find((p) => String(p.idprofil) === String(idprofil));
    return profil ? profil.libprofil : 'Non défini';
  };

  /* ─── Actions ─── */

  const closeForm = useCallback(() => {
    setShowModal(false);
    setEditData(null);
    setFormData(EMPTY_FORM);
  }, []);
  const closeAttach = useCallback(() => setAttachUser(null), []);
  const closeDelete = useCallback(() => setDeleteTarget(null), []);

  const openCreate = () => {
    setEditData(null);
    setFormData({ ...EMPTY_FORM, filiale_id: isConsolidated ? '' : filialeId || '' });
    setShowModal(true);
  };

  const handleEdit = (u) => {
    setEditData(u);
    setFormData({
      username: u.username || '',
      email: u.email || '',
      password: '',
      nom: u.nom || '',
      prenom: u.prenom || '',
      idprofil: u.idprofil || '',
      role: u.role || 'user',
      filiale_id: u.filiale_id || '',
      personnel_id: u.personnel_id || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...formData };
      if (editData && !payload.password) delete payload.password;
      if (editData) await http.put(`/api/users/${editData.iduser}`, payload);
      else await http.post('/api/users', payload);
      closeForm();
      setRefreshKey((k) => k + 1);
      showToast('success', editData ? 'Utilisateur modifié' : 'Utilisateur créé');
    } catch (error) {
      showToast('error', "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await http.delete(`/api/users/${deleteTarget.iduser}`);
      setDeleteTarget(null);
      setRefreshKey((k) => k + 1);
      showToast('success', 'Utilisateur supprimé');
    } catch (error) {
      showToast('error', 'Erreur lors de la suppression');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (u) => {
    try {
      const newStatus = isUserActive(u) ? 1 - ETAT_ACTIF : ETAT_ACTIF;
      await http.put(`/api/users/${u.iduser}`, { etat: newStatus });
      setRefreshKey((k) => k + 1);
      showToast('success', isUserActive(u) ? 'Compte désactivé' : 'Compte activé');
    } catch (error) {
      showToast('error', 'Erreur lors de la modification du statut');
    }
  };

  const confirmAttach = async (personnelId) => {
    if (!attachUser) return;
    setSaving(true);
    try {
      await http.put(`/api/users/${attachUser.iduser}`, { personnel_id: personnelId });
      const emp = personnelById[String(personnelId)];
      setJustLinked(attachUser.iduser);
      showToast(
        'success',
        `${attachUser.prenom || attachUser.nom} est rattaché${emp ? ` à ${emp.prenom || ''} ${emp.nom || ''}`.trimEnd() : ''}`
      );
      setAttachUser(null);
      setRefreshKey((k) => k + 1);
    } catch (error) {
      showToast('error', 'Erreur lors du rattachement');
    } finally {
      setSaving(false);
    }
  };

  const detachUser = async () => {
    if (!attachUser) return;
    setSaving(true);
    try {
      await http.put(`/api/users/${attachUser.iduser}`, { personnel_id: null });
      showToast('success', 'Rattachement supprimé');
      setAttachUser(null);
      setRefreshKey((k) => k + 1);
    } catch (error) {
      showToast('error', 'Erreur lors du détachement');
    } finally {
      setSaving(false);
    }
  };

  const toggleAttachFilter = (value) => setAttachFilter((cur) => (cur === value ? '' : value));

  /* ═══════════════════════════ Rendu ═══════════════════════════ */

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/40">
      <style>{`
        @keyframes u-fade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes u-rise { from { opacity: 0; transform: translateY(18px) scale(.97) } to { opacity: 1; transform: none } }
        @keyframes u-flow { to { background-position: 20px 0 } }
        @keyframes u-pop { 0% { transform: scale(.3); opacity: 0 } 60% { transform: scale(1.18) } 100% { transform: scale(1); opacity: 1 } }
        @keyframes u-ping { 0% { transform: scale(1); opacity: .7 } 100% { transform: scale(1.9); opacity: 0 } }
        @keyframes u-shine { from { left: -40% } to { left: 120% } }
        @keyframes u-grow { from { transform: scaleX(0); transform-origin: left } to { transform: scaleX(1); transform-origin: left } }
        @keyframes u-flash { 0%,100% { box-shadow: 0 0 0 0 rgba(16,185,129,0) } 30% { box-shadow: 0 0 0 6px rgba(16,185,129,.35) } }
        @keyframes u-toast { from { opacity: 0; transform: translateY(14px) scale(.96) } to { opacity: 1; transform: none } }
        .u-fade { animation: u-fade 180ms ease-out }
        .u-rise { animation: u-rise 260ms cubic-bezier(.16,1,.3,1) }
        .u-flow { animation: u-flow 900ms linear infinite }
        .u-pop { animation: u-pop 420ms cubic-bezier(.34,1.56,.64,1) }
        .u-ping { animation: u-ping 1.6s ease-out infinite }
        .u-shine { animation: u-shine 1.8s ease-in-out infinite }
        .u-grow { animation: u-grow 500ms cubic-bezier(.22,1,.36,1) }
        .u-flash { animation: u-flash 1.3s ease-out 2 }
        .u-toast { animation: u-toast 260ms cubic-bezier(.16,1,.3,1) }
        @media (prefers-reduced-motion: reduce) {
          .u-fade,.u-rise,.u-flow,.u-pop,.u-ping,.u-shine,.u-grow,.u-flash,.u-toast { animation: none }
        }
      `}</style>

      <BlueHeader
        title="Gestion des Utilisateurs"
        subtitle="Comptes de la console ZEUS SIRH"
        icon={UsersIcon}
        showBack={false}
      />

      <div className="px-6 py-8">
        {/* ── Statistiques ── */}
        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard label="Total utilisateurs" value={stats.total} icon={UsersIcon} tone="blue" />
          <StatCard
            label="Utilisateurs actifs"
            value={stats.active}
            icon={Check}
            tone="emerald"
            hint={stats.total ? `${Math.round((stats.active / stats.total) * 100)} % du total` : undefined}
          />
          <StatCard
            label="Rattachés à un employé"
            value={stats.attached}
            icon={UserCheck}
            tone="sky"
            onClick={() => toggleAttachFilter('attached')}
            active={attachFilter === 'attached'}
          >
            <div className="mt-4">
              <div className="flex justify-between text-[11px] font-medium text-slate-400">
                <span>Taux de rattachement</span>
                <span className="text-slate-600">{stats.rate} %</span>
              </div>
              <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-sky-500 to-emerald-500 transition-all duration-700"
                  style={{ width: `${stats.rate}%` }}
                />
              </div>
            </div>
          </StatCard>
          <StatCard
            label="À rattacher"
            value={stats.unattached}
            icon={AlertCircle}
            tone="amber"
            hint="Cliquer pour filtrer"
            onClick={() => toggleAttachFilter('unattached')}
            active={attachFilter === 'unattached'}
          />
        </div>

        {/* ── Barre d'outils ── */}
        <div className="mb-6 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Rechercher un nom, un email, un identifiant…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`${inputCls} pl-10`}
              />
            </div>
            <select
              value={profilFilter}
              onChange={(e) => setProfilFilter(e.target.value)}
              className={`${inputCls} lg:w-56`}
            >
              <option value="">Tous les profils</option>
              {profils.map((p) => (
                <option key={p.idprofil} value={p.idprofil}>{p.libprofil}</option>
              ))}
            </select>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={openCreate}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-500/25 transition hover:shadow-xl lg:flex-none"
              >
                <Plus className="h-4 w-4" /> Nouvel utilisateur
              </button>
              <button
                type="button"
                onClick={() => setRefreshKey((k) => k + 1)}
                aria-label="Actualiser"
                className="rounded-xl border border-slate-200 bg-white px-3.5 text-slate-600 transition hover:bg-slate-50"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Filtres par rôle */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {[{ value: '', label: 'Tous', count: stats.total }, ...Object.keys(ROLE_META).map((r) => ({
              value: r, label: roleLabel(r), count: roleCounts[r] || 0,
            }))].map((chip) => (
              <button
                key={chip.value || 'all'}
                type="button"
                onClick={() => setRoleFilter(chip.value)}
                className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm font-medium transition ${
                  roleFilter === chip.value
                    ? 'border-blue-600 bg-blue-600 text-white shadow-sm'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-blue-300 hover:text-blue-700'
                }`}
              >
                {chip.label}
                <span
                  className={`rounded-full px-1.5 text-[11px] font-semibold ${
                    roleFilter === chip.value ? 'bg-white/25 text-white' : 'bg-slate-100 text-slate-500'
                  }`}
                >
                  {chip.count}
                </span>
              </button>
            ))}
            {attachFilter && (
              <button
                type="button"
                onClick={() => setAttachFilter('')}
                className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700 ring-1 ring-amber-200 transition hover:bg-amber-100"
              >
                {attachFilter === 'attached' ? 'Rattachés uniquement' : 'Non rattachés uniquement'}
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* ── Liste ── */}
        {loading ? (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-72 animate-pulse rounded-3xl border border-slate-200 bg-white" />
            ))}
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-slate-300 bg-white p-14 text-center">
            <UsersIcon className="mx-auto mb-3 h-14 w-14 text-slate-300" />
            <p className="font-semibold text-slate-600">Aucun utilisateur trouvé</p>
            <p className="mt-1 text-sm text-slate-400">Modifiez vos filtres ou créez un nouveau compte.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
              {paginatedUsers.map((u) => {
              const meta = ROLE_META[u.role] || ROLE_META.user;
              const active = isUserActive(u);
              const attached = !!u.personnel_id;
              const linked = attached ? personnelById[String(u.personnel_id)] : null;
              const flash = justLinked === u.iduser;

              return (
                <div
                  key={u.iduser}
                  className={`group relative overflow-hidden rounded-3xl border bg-white shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl ${
                    flash ? 'u-flash border-emerald-400' : 'border-slate-200'
                  }`}
                >
                  <div className={`h-1.5 bg-gradient-to-r ${meta.bar}`} />
                  <div className="p-5">
                    {/* En-tête carte */}
                    <div className="flex items-start gap-3">
                      <div className="relative">
                        <Avatar nom={u.nom} prenom={u.prenom} seed={u.iduser} />
                        <span
                          title={active ? 'Compte actif' : 'Compte désactivé'}
                          className={`absolute -bottom-1 -right-1 h-4 w-4 rounded-full border-2 border-white ${
                            active ? 'bg-emerald-500' : 'bg-slate-300'
                          }`}
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="truncate font-semibold text-slate-900">{u.nom} {u.prenom}</h3>
                        <p className="flex items-center gap-1.5 truncate text-sm text-slate-500">
                          <Mail className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">{u.email || u.username}</span>
                        </p>
                      </div>
                      <span
                        className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ${meta.badge}`}
                      >
                        <Shield className="h-3 w-3" />
                        {roleLabel(u.role)}
                      </span>
                    </div>

                    {/* Infos */}
                    <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
                      <div className="rounded-xl bg-slate-50 px-3 py-2">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Profil</p>
                        <p className="truncate font-medium text-slate-700">{getProfilLabel(u.idprofil)}</p>
                      </div>
                      <div className="rounded-xl bg-slate-50 px-3 py-2">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Filiale</p>
                        <p className="flex items-center gap-1 truncate font-medium text-slate-700">
                          <Building2 className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                          <span className="truncate">{u.filiale_libelle || u.pays_nom || 'Groupe'}</span>
                        </p>
                      </div>
                    </div>

                    {/* Rattachement */}
                    {attached ? (
                      <div className="mt-4 rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-3">
                        <div className="flex items-center gap-3">
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-500 text-white shadow">
                            <Link2 className="h-4 w-4" />
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700">
                              Rattaché à
                            </p>
                            <p className="truncate text-sm font-semibold text-slate-900">
                              {linked ? `${linked.nom} ${linked.prenom}` : 'Fiche employé liée'}
                            </p>
                            {linked?.matricule && (
                              <p className="font-mono text-[11px] text-slate-500">{linked.matricule}</p>
                            )}
                          </div>
                          {canAttach && (
                            <button
                              type="button"
                              onClick={() => setAttachUser(u)}
                              className="rounded-lg px-2.5 py-1.5 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-100"
                            >
                              Changer
                            </button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="mt-4 rounded-2xl border-2 border-dashed border-amber-300/70 bg-amber-50/50 p-3">
                        <div className="flex items-center gap-3">
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-600">
                            <Unlink className="h-4 w-4" />
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-amber-800">Aucun employé rattaché</p>
                            <p className="text-xs text-amber-700/70">Ce compte n'est lié à aucune fiche.</p>
                          </div>
                        </div>
                        {canAttach && (
                          <button
                            type="button"
                            onClick={() => setAttachUser(u)}
                            className="group/btn mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-blue-500/25 transition hover:shadow-lg"
                          >
                            <Sparkles className="h-4 w-4" />
                            Rattacher maintenant
                            <ArrowRight className="h-4 w-4 transition-transform group-hover/btn:translate-x-1" />
                          </button>
                        )}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="mt-4 flex items-center gap-2 border-t border-slate-100 pt-4">
                      <button
                        type="button"
                        onClick={() => handleEdit(u)}
                        className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-200"
                      >
                        <Pencil className="h-4 w-4" /> Modifier
                      </button>
                      <button
                        type="button"
                        onClick={() => handleToggleStatus(u)}
                        title={active ? 'Désactiver' : 'Activer'}
                        aria-label={active ? 'Désactiver' : 'Activer'}
                        className={`rounded-xl p-2.5 transition ${
                          active
                            ? 'bg-amber-50 text-amber-600 hover:bg-amber-100'
                            : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'
                        }`}
                      >
                        {active ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteTarget(u)}
                        title="Supprimer"
                        aria-label="Supprimer"
                        className="rounded-xl bg-red-50 p-2.5 text-red-600 transition hover:bg-red-100"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── Pagination ── */}
          {totalPages > 1 && (
            <div className="mt-8 flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-slate-600 transition hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  type="button"
                  onClick={() => setCurrentPage(page)}
                  className={`min-w-[40px] rounded-xl px-4 py-2 text-sm font-semibold transition ${
                    currentPage === page
                      ? 'bg-blue-600 text-white shadow-md shadow-blue-500/25'
                      : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {page}
                </button>
              ))}

              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-slate-600 transition hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
          </>
        )}
      </div>

      {/* ═══════ Modale création / modification ═══════ */}
      {showModal && (
        <Modal onClose={closeForm} maxWidth="max-w-2xl" labelledBy="user-form-title">
          <div className="flex items-center justify-between gap-4 border-b border-slate-100 px-6 py-5">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-600 text-white shadow-lg shadow-blue-500/30">
                <UsersIcon className="h-5 w-5" />
              </span>
              <div>
                <h2 id="user-form-title" className="text-lg font-bold text-slate-900">
                  {editData ? "Modifier l'utilisateur" : 'Nouvel utilisateur'}
                </h2>
                <p className="text-sm text-slate-500">
                  {editData ? 'Mettez à jour les informations du compte.' : 'Créez un compte pour la console.'}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={closeForm}
              aria-label="Fermer"
              className="rounded-xl p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
            <div className="flex-1 space-y-6 overflow-y-auto px-6 py-6">
              <section>
                <p className="mb-3 text-xs font-bold uppercase tracking-wider text-blue-600">Identité</p>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label="Nom">
                    <input
                      type="text" required className={inputCls} value={formData.nom}
                      onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    />
                  </Field>
                  <Field label="Prénom">
                    <input
                      type="text" required className={inputCls} value={formData.prenom}
                      onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                    />
                  </Field>
                </div>
              </section>

              <section>
                <p className="mb-3 text-xs font-bold uppercase tracking-wider text-blue-600">Connexion</p>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label="Email">
                    <input
                      type="email" required className={inputCls} value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </Field>
                  <Field label="Nom d'utilisateur">
                    <input
                      type="text" required className={inputCls} value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    />
                  </Field>
                  {!editData && (
                    <div className="sm:col-span-2">
                      <Field label="Mot de passe (optionnel)">
                        <input
                          type="password" className={inputCls} value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          placeholder="Laisser vide pour utiliser le mot de passe par défaut"
                        />
                        <p className="mt-1 text-xs text-slate-400">Vous pourrez définir le mot de passe ultérieurement</p>
                      </Field>
                    </div>
                  )}
                </div>
              </section>

              <section>
                <p className="mb-3 text-xs font-bold uppercase tracking-wider text-blue-600">Accès</p>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label="Rôle">
                    <select
                      className={inputCls} value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    >
                      <option value="user">Utilisateur</option>
                      <option value="manager">Manager</option>
                      <option value="admin_pays">Admin Pays</option>
                      {isSuperAdmin && <option value="super_admin">Super Admin</option>}
                    </select>
                  </Field>
                  <Field label="Profil">
                    <select
                      className={inputCls} value={formData.idprofil}
                      onChange={(e) => setFormData({ ...formData, idprofil: e.target.value })}
                    >
                      <option value="">Aucun profil</option>
                      {profils.map((p) => (
                        <option key={p.idprofil} value={p.idprofil}>{p.libprofil}</option>
                      ))}
                    </select>
                  </Field>
                  {isSuperAdmin && (
                    <div className="sm:col-span-2">
                      <Field label="Filiale">
                        <select
                          className={inputCls} value={formData.filiale_id}
                          onChange={(e) => setFormData({ ...formData, filiale_id: e.target.value })}
                        >
                          <option value="">Groupe (toutes filiales)</option>
                          {filialeOptions.map((f) => (
                            <option key={f.id} value={f.id}>{f.label}</option>
                          ))}
                        </select>
                      </Field>
                    </div>
                  )}
                </div>
              </section>
            </div>

            <div className="flex gap-3 border-t border-slate-100 bg-slate-50/80 px-6 py-4">
              <button
                type="button" onClick={closeForm}
                className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
              >
                Annuler
              </button>
              <button
                type="submit" disabled={saving}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-500/25 transition hover:shadow-xl disabled:opacity-60"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {editData ? 'Enregistrer' : 'Créer le compte'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* ═══════ Modale de rattachement ═══════ */}
      {attachUser && (
        <AttachModal
          user={attachUser}
          personnel={personnel}
          takenBy={takenBy}
          saving={saving}
          onClose={closeAttach}
          onConfirm={confirmAttach}
          onDetach={detachUser}
        />
      )}

      {/* ═══════ Modale de suppression ═══════ */}
      {deleteTarget && (
        <Modal onClose={closeDelete} maxWidth="max-w-md" labelledBy="delete-title">
          <div className="p-6 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-red-100 text-red-600">
              <Trash2 className="h-6 w-6" />
            </span>
            <h2 id="delete-title" className="mt-4 text-lg font-bold text-slate-900">Supprimer cet utilisateur ?</h2>
            <p className="mt-1 text-sm text-slate-500">
              Le compte de <span className="font-semibold text-slate-700">{deleteTarget.nom} {deleteTarget.prenom}</span> sera
              définitivement supprimé.
            </p>
            <div className="mt-6 flex gap-3">
              <button
                type="button" onClick={closeDelete}
                className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
              >
                Annuler
              </button>
              <button
                type="button" onClick={handleDelete} disabled={saving}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700 disabled:opacity-60"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                Supprimer
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ═══════ Notification ═══════ */}
      {toast && (
        <div
          key={toast.id}
          role="status"
          className={`u-toast fixed bottom-6 right-6 z-[60] flex max-w-sm items-center gap-3 rounded-2xl px-4 py-3 text-sm font-medium text-white shadow-2xl ${
            toast.type === 'success' ? 'bg-slate-900' : 'bg-red-600'
          }`}
        >
          <span
            className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
              toast.type === 'success' ? 'bg-emerald-500' : 'bg-white/25'
            }`}
          >
            {toast.type === 'success' ? <Check className="h-4 w-4" strokeWidth={3} /> : <AlertCircle className="h-4 w-4" />}
          </span>
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default Users;